/**
 * EDR write-path mutations. The demo EDR backend does not (yet) expose
 * write endpoints, so each mutation simulates a round-trip with a small
 * delay, shows a `sonner` toast, and invalidates the relevant query
 * keys so consumers refresh optimistically. When the real endpoints
 * land, swap the `simulate()` body for `edrPost(...)`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

function simulate<T>(value: T, latencyMs = 500): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), latencyMs));
}

type CaseAction = "assign" | "close" | "escalate";

export function useCaseAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { caseId: string; action: CaseAction; assignee?: string }) =>
      simulate(vars),
    onSuccess: (vars) => {
      const labels: Record<CaseAction, string> = {
        assign: vars.assignee ? `Assigned to ${vars.assignee}` : "Assigned to you",
        close: "Case closed",
        escalate: "Case escalated",
      };
      toast.success(labels[vars.action]);
      qc.invalidateQueries({ queryKey: ["edr"] });
    },
    onError: (e: Error) => toast.error(e.message || "Action failed"),
  });
}

type DeviceAction = "isolate" | "reboot" | "release-isolation";

export function useDeviceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { deviceId: string; action: DeviceAction }) => simulate(vars),
    onSuccess: (vars) => {
      const labels: Record<DeviceAction, string> = {
        isolate: "Sensor isolated",
        reboot: "Reboot requested",
        "release-isolation": "Isolation lifted",
      };
      toast.success(labels[vars.action]);
      qc.invalidateQueries({ queryKey: ["edr"] });
    },
    onError: (e: Error) => toast.error(e.message || "Action failed"),
  });
}

type QuarantineAction = "release" | "delete" | "download";

export function useQuarantineAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { itemId: string; action: QuarantineAction }) => simulate(vars),
    onSuccess: (vars) => {
      const labels: Record<QuarantineAction, string> = {
        release: "File released",
        delete: "File deleted",
        download: "Download starting",
      };
      toast.success(labels[vars.action]);
      qc.invalidateQueries({ queryKey: ["edr", "quarantine"] });
    },
    onError: (e: Error) => toast.error(e.message || "Action failed"),
  });
}

export interface CreateBinAuthRuleInput {
  match: "team-id" | "cdhash" | "signing-cert" | "path";
  value: string;
  mode: "allow" | "deny" | "dry-run-deny";
  comment?: string;
}

export function useCreateBinAuthRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBinAuthRuleInput) => simulate(input, 700),
    onSuccess: () => {
      toast.success("Rule created");
      qc.invalidateQueries({ queryKey: ["edr", "binauth"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not create rule"),
  });
}

export function useQuarantineHash() {
  return useMutation({
    mutationFn: async (vars: { sha256: string }) => simulate(vars, 800),
    onSuccess: (vars) => toast.success(`Quarantine requested for ${vars.sha256.slice(0, 12)}…`),
    onError: (e: Error) => toast.error(e.message || "Could not quarantine"),
  });
}
